import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Camera, CheckCircle2, FileUp, Image as ImageIcon, RefreshCw, Upload, XCircle } from 'lucide-react';
import { ownerImages } from '../../data/ownerProfile';
export type UploadState = 'uploaded' | 'missing' | 'expired';
interface UploadTileProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  state: UploadState;
  fileName?: string;
  onUpload: (source: string) => void;
  preview?: string;
  children?: React.ReactNode;
}
const stateView = {
  uploaded: {
    icon: CheckCircle2,
    text: 'আপলোড সম্পন্ন',
    cls: 'text-emerald-700 bg-emerald-50 border-emerald-200'
  },
  missing: {
    icon: AlertTriangle,
    text: 'আপলোড করা হয়নি',
    cls: 'text-amber-700 bg-amber-50 border-amber-200'
  },
  expired: {
    icon: XCircle,
    text: 'মেয়াদোত্তীর্ণ',
    cls: 'text-red-700 bg-red-50 border-red-200'
  }
};
export function UploadTile({
  label,
  required,
  optional,
  state,
  fileName,
  onUpload,
  preview,
  children
}: UploadTileProps) {
  const [open, setOpen] = useState(false);
  const v = stateView[state];
  const pick = (src: string) => {
    setOpen(false);
    onUpload(src);
  };
  return <div className={`rounded-xl border bg-white p-3 shadow-sm ${state === 'expired' ? 'border-red-200' : state === 'missing' && required ? 'border-amber-200' : 'border-border'}`} data-id="element-2180" data-mp="2x1ucclmy0jo1">
      <div className="flex items-start gap-3" data-id="element-2181" data-mp="2s0n1uetf16do">
        <div className="flex h-16 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50" data-id="element-2182" data-mp="2n3wo3y532mvf">
          {state !== 'missing' ? <img src={preview ?? ownerImages.docScan} alt="" className="h-full w-full object-cover" data-id="element-2183" data-mp="2e2yk3funafcu" /> : <ImageIcon className="h-6 w-6 text-slate-300" data-id="element-2184" data-mp="2e4t3uqu6k1fc" />}
        </div>
        <div className="min-w-0 flex-1" data-id="element-2185" data-mp="295gbjqkji25d">
          <p className="text-sm font-semibold text-navy-900" data-id="element-2186" data-mp="cbd3h28aj876">
            {label} {required && <span className="text-red-600" data-id="element-2187" data-mp="c990qnm4of8h">*</span>}
            {optional && <span className="ml-1 text-[11px] font-normal text-slate-400" data-id="element-2188" data-mp="7d1u0slwqzph">(প্রযোজ্য হলে)</span>}
          </p>
          <span className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${v.cls}`} data-id="element-2189" data-mp="21rfn4d8hxsoo">
            <v.icon className="h-3 w-3" data-id="element-2190" data-mp="1rukto8r2yih9" />
            {v.text}
          </span>
          {fileName && state !== 'missing' && <p className="mt-1 truncate font-mono text-[10.5px] text-slate-400" data-id="element-2191" data-mp="1wt4da2i7bi6g">{fileName}</p>}
        </div>
        <button onClick={() => setOpen(o => !o)} className="flex shrink-0 items-center gap-1 rounded-lg border border-navy-200 px-2.5 py-1.5 text-xs font-semibold text-navy-700 hover:bg-navy-50" aria-expanded={open} data-id="element-2192" data-mp="2kiyq4k2it6qp">
          {state === 'missing' ? <Upload className="h-3.5 w-3.5" data-id="element-2193" data-mp="2aksqya83mepj" /> : <RefreshCw className="h-3.5 w-3.5" data-id="element-2194" data-mp="2fjvjwwat0uh5" />}
          {state === 'missing' ? 'আপলোড' : 'পুনরায়'}
        </button>
      </div>
      {children}
      <AnimatePresence data-id="element-2195" data-mp="z53yx4fzmphv">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-2196" data-mp="144yu3k6sq18q">
            <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3" data-id="element-2197" data-mp="3ib6jgkda16xd">
              {[{
            icon: Camera,
            label: 'ক্যামেরা দিয়ে ছবি তুলুন',
            src: 'camera'
          }, {
            icon: ImageIcon,
            label: 'গ্যালারি থেকে নির্বাচন',
            src: 'gallery'
          }, {
            icon: FileUp,
            label: 'PDF আপলোড করুন',
            src: 'pdf'
          }].map(a => <button key={a.src} onClick={() => pick(a.src)} className="flex flex-col items-center gap-1 rounded-lg bg-slate-50 p-2 text-center text-[10.5px] font-medium leading-tight text-navy-800 hover:bg-navy-50" data-id="element-2198" data-mp="2znb8f891u77y">
                  <a.icon className="h-5 w-5" data-id="element-2199" data-mp="38g4uboj044us" />
                  {a.label}
                </button>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}